import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  useTheme,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import { connect } from 'redux-bundler-react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import React from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import ParticleBurst, {
  GREEN_PALETTE,
} from '../../atoms/ParticleBurst';

const STATE_LABELS = {
  building: 'Building',
  succeeded: 'Deployed',
  failed: 'Failed',
};

const accentForState = (theme, state) => {
  if (state === 'succeeded') return theme.palette.success.main;
  if (state === 'failed') return theme.palette.warning.main;
  return theme.palette.info.main;
};

const BranchIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M5 3v9.5M5 9c5 0 6-1 6-3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="5" cy="3" r="1.6" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="5" cy="13" r="1.6" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="11" cy="4" r="1.6" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const DeployInProgressNotification = ({
  deployProgressState: state,
  deployProgressTitle: title,
  deployProgressDeployBranch: deployBranch,
  deployProgressCommitRef: commitRef,
  deployProgressIsCurrentBranch: isCurrentBranch,
  doDismissDeployProgress,
  doReloadForDeployProgress,
  doSwitchToDeployBranch,
}) => {
  const theme = useTheme();
  const open = Boolean(state);
  const accent = accentForState(theme, state);
  const isDark = theme.palette.mode === 'dark';

  const [bursts, setBursts] = React.useState([]);
  const prevStateRef = React.useRef(null);
  const triggerBurst = React.useCallback(() => {
    const id = `${Date.now()}-${Math.random()}`;
    setBursts(b => [...b, id]);
    setTimeout(() => {
      setBursts(b => b.filter(x => x !== id));
    }, 1700);
  }, []);

  React.useEffect(() => {
    if (state === 'succeeded' && prevStateRef.current !== 'succeeded') {
      triggerBurst();
    }
    prevStateRef.current = state;
  }, [state, triggerBurst]);

  React.useEffect(() => {
    if (!open) return undefined;
    window.__deployBurst = triggerBurst;
    return () => {
      if (window.__deployBurst === triggerBurst) {
        delete window.__deployBurst;
      }
    };
  }, [open, triggerBurst]);

  const renderIcon = () => {
    if (state === 'building') {
      return (
        <CircularProgress
          size={22}
          thickness={4}
          sx={{ color: accent }}
        />
      );
    }
    if (state === 'succeeded') {
      return (
        <CheckCircleOutlineIcon sx={{ color: accent, fontSize: 24 }} />
      );
    }
    if (state === 'failed') {
      return <WarningAmberIcon sx={{ color: accent, fontSize: 24 }} />;
    }
    return null;
  };

  const renderActions = () => {
    if (state === 'building' || state === 'failed') {
      return (
        <Button
          size="small"
          onClick={doDismissDeployProgress}
          variant="outlined"
          sx={{
            color: accent,
            borderColor: alpha(accent, 0.5),
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: accent,
              backgroundColor: alpha(accent, 0.08),
            },
          }}
        >
          Dismiss
        </Button>
      );
    }
    if (state === 'succeeded' && isCurrentBranch) {
      return (
        <>
          <Button
            size="small"
            onClick={doDismissDeployProgress}
            variant="outlined"
          sx={{
            color: accent,
            borderColor: alpha(accent, 0.5),
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: accent,
              backgroundColor: alpha(accent, 0.08),
            },
          }}
          >
            Later
          </Button>
          <Button
            size="small"
            onClick={doReloadForDeployProgress}
            variant="outlined"
          sx={{
            color: accent,
            borderColor: alpha(accent, 0.5),
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: accent,
              backgroundColor: alpha(accent, 0.08),
            },
          }}
          >
            Reload
          </Button>
        </>
      );
    }
    if (state === 'succeeded' && !isCurrentBranch) {
      return (
        <>
          <Button
            size="small"
            onClick={doDismissDeployProgress}
            variant="outlined"
          sx={{
            color: accent,
            borderColor: alpha(accent, 0.5),
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: accent,
              backgroundColor: alpha(accent, 0.08),
            },
          }}
          >
            Dismiss
          </Button>
          <Button
            size="small"
            onClick={doSwitchToDeployBranch}
            variant="outlined"
          sx={{
            color: accent,
            borderColor: alpha(accent, 0.5),
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: accent,
              backgroundColor: alpha(accent, 0.08),
            },
          }}
          >
            Switch
          </Button>
        </>
      );
    }
    return null;
  };

  const shortCommit = commitRef ? commitRef.slice(0, 7) : null;
  const stateLabel = STATE_LABELS[state];

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      open={open}
      sx={{
        '& .MuiSnackbarContent-root': { padding: 0 },
        maxWidth: 720,
        width: 'calc(100vw - 32px)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
        }}
      >
        {bursts.map(id => (
          <ParticleBurst
            key={id}
            pattern="strip"
            colors={GREEN_PALETTE}
            duration={1300}
            distanceScale={1.4}
          />
        ))}
        <Paper
          elevation={isDark ? 6 : 2}
          sx={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1.5,
            backgroundColor: isDark
              ? darken(accent, 0.82)
              : lighten(accent, 0.9),
            border: `1px solid ${alpha(accent, isDark ? 0.5 : 0.4)}`,
            color: theme.palette.text.primary,
          }}
        >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            flexShrink: 0,
          }}
        >
          {renderIcon()}
        </Box>

        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Box
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: theme.palette.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title || 'Deploy'}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mt: 0.25,
              fontSize: 12,
              color: theme.palette.text.secondary,
              flexWrap: 'wrap',
            }}
          >
            <Box component="span" sx={{ color: accent, fontWeight: 500 }}>
              {stateLabel}
            </Box>
            {deployBranch && (
              <>
                <Box component="span" sx={{ opacity: 0.6 }}>
                  ·
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontFamily: theme.typography.monospace?.fontFamily ||
                      'Menlo, monospace',
                  }}
                >
                  <BranchIcon />
                  {deployBranch}
                </Box>
              </>
            )}
            {shortCommit && (
              <>
                <Box component="span" sx={{ opacity: 0.6 }}>
                  ·
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontFamily: theme.typography.monospace?.fontFamily ||
                      'Menlo, monospace',
                  }}
                >
                  {shortCommit}
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
          }}
        >
          {renderActions()}
        </Box>
        </Paper>
      </Box>
    </Snackbar>
  );
};

export default connect(
  'selectDeployProgressState',
  'selectDeployProgressTitle',
  'selectDeployProgressDeployBranch',
  'selectDeployProgressCommitRef',
  'selectDeployProgressIsCurrentBranch',
  'doDismissDeployProgress',
  'doReloadForDeployProgress',
  'doSwitchToDeployBranch',
  DeployInProgressNotification,
);
